
export default function ProgramsListSection() {
  const programs = [
    {
      title: "Bachillerato en Ciencias",
      description: "Formación integral en ciencias básicas con énfasis en matemáticas, física, química y biología. Este programa prepara a los estudiantes para carreras científicas y tecnológicas, desarrollando habilidades de investigación y análisis crítico.",
      features: ["Matemáticas Avanzadas", "Física Experimental", "Química Orgánica", "Biología Molecular", "Estadística Aplicada", "Metodología de la Investigación"],
      icon: "ri-flask-line",
      color: "blue",
      universities: ["Universidad de Chile", "Universidad de Concepción", "Universidad Austral de Chile"]
    },
    {
      title: "Bachillerato en Humanidades",
      description: "Desarrollo del pensamiento crítico a través de filosofía, literatura, historia y ciencias sociales. Forma estudiantes con una sólida base humanística y capacidad de análisis social y cultural.",
      features: ["Filosofía Contemporánea", "Literatura Universal", "Historia de Chile", "Sociología", "Antropología Cultural", "Ética y Valores"],
      icon: "ri-book-open-line",
      color: "purple",
      universities: ["Universidad de Santiago", "Pontificia Universidad Católica", "Universidad de Valparaíso"]
    },
    {
      title: "Bachillerato en Ciencias de la Ingeniería",
      description: "Base sólida en ciencias aplicadas y matemáticas para carreras de ingeniería. Combina fundamentos teóricos con aplicaciones prácticas en tecnología e innovación.",
      features: ["Cálculo Diferencial", "Física Aplicada", "Programación", "Diseño Técnico", "Mecánica", "Electromagnetismo"],
      icon: "ri-tools-line",
      color: "green",
      universities: ["Universidad de La Frontera", "Universidad Católica del Norte", "Universidad de Antofagasta"]
    },
    {
      title: "Bachillerato Interdisciplinario",
      description: "Enfoque transversal que integra ciencias, humanidades y artes. Promueve una visión holística del conocimiento y desarrolla competencias para abordar problemas complejos.",
      features: ["Pensamiento Sistémico", "Arte y Cultura", "Ética Aplicada", "Innovación Social", "Sustentabilidad", "Comunicación Efectiva"],
      icon: "ri-palette-line",
      color: "orange",
      universities: ["Universidad de Santiago", "Universidad de Chile", "Universidad de Concepción"]
    }
  ];

  const colorClasses = {
    blue: {
      bg: "bg-blue-100",
      text: "text-blue-600",
      border: "border-blue-200",
      gradient: "from-blue-50 to-blue-100",
      accent: "bg-blue-500"
    },
    purple: {
      bg: "bg-purple-100",
      text: "text-purple-600",
      border: "border-purple-200",
      gradient: "from-purple-50 to-purple-100",
      accent: "bg-purple-500"
    },
    green: {
      bg: "bg-green-100",
      text: "text-green-600",
      border: "border-green-200",
      gradient: "from-green-50 to-green-100",
      accent: "bg-green-500"
    },
    orange: {
      bg: "bg-orange-100",
      text: "text-orange-600",
      border: "border-orange-200",
      gradient: "from-orange-50 to-orange-100",
      accent: "bg-orange-500"
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Nuestros Programas
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cada programa está diseñado para proporcionar una formación integral que combina 
              excelencia académica con desarrollo personal y profesional
            </p>
          </div>

          <div className="space-y-12">
            {programs.map((program, index) => (
              <div 
                key={index}
                className={`bg-gradient-to-br ${colorClasses[program.color as keyof typeof colorClasses].gradient} rounded-3xl p-8 md:p-12 shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-center mb-6">
                      <div className={`w-16 h-16 ${colorClasses[program.color as keyof typeof colorClasses].bg} rounded-2xl flex items-center justify-center mr-4`}>
                        <i className={`${program.icon} ${colorClasses[program.color as keyof typeof colorClasses].text} text-2xl`}></i>
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">
                        {program.title}
                      </h3>
                    </div>
                    
                    <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                      {program.description}
                    </p>
                    
                    <div className="mb-8">
                      <h4 className="font-semibold text-gray-900 mb-4 text-lg">Áreas de Estudio:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {program.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center space-x-3">
                            <div className={`w-6 h-6 ${colorClasses[program.color as keyof typeof colorClasses].bg} rounded-full flex items-center justify-center`}>
                              <i className={`ri-check-line ${colorClasses[program.color as keyof typeof colorClasses].text} text-sm`}></i>
                            </div>
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-2xl p-6 shadow-lg">
                      <h4 className="font-semibold text-gray-900 mb-4">Universidades que lo ofrecen:</h4>
                      <div className="space-y-3">
                        {program.universities.map((university, uniIndex) => (
                          <div key={uniIndex} className="flex items-center space-x-3">
                            <div className={`w-8 h-8 ${colorClasses[program.color as keyof typeof colorClasses].accent} rounded-lg flex items-center justify-center`}>
                              <i className="ri-building-line text-white text-sm"></i>
                            </div>
                            <span className="text-gray-700 text-sm">{university}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-900 mb-1">4 años</div>
                          <div className="text-gray-600 text-sm">Duración del programa</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
